import { Report, ReportCategory } from "@fundwave/openapi-client/dist/models";
import { ReportAPI } from "../apiclient/report";
import { ReportAPIClient } from "@fundwave/openapi-client";

export default class ReportUtil {
  static async saveReportToFW(fundId: string, filename: string, reportTypeId?: string) {
    const report: Report = {
      group: { name: filename },
      category: ReportCategory.Functions,
      fundId: +fundId
    };

    if (reportTypeId) report.type = { id: reportTypeId };

    await ReportUtil.changeSheetVisibility("Installation Guide", Excel.SheetVisibility.hidden);

    const pdfFileId = await ReportUtil.getDocumentAndUpload(filename, Office.FileType.Pdf, "pdf");
    const excelFileId = await ReportUtil.getDocumentAndUpload(filename, Office.FileType.Compressed, "xlsx");

    await ReportUtil.changeSheetVisibility("Installation Guide", Excel.SheetVisibility.visible);

    if (!excelFileId && !pdfFileId) throw Error("Unable to upload file");
    if (!!excelFileId) report.xlsxFile = { id: excelFileId };
    if (!!pdfFileId) report.pdfFile = { id: pdfFileId };

    await ReportAPIClient.addReport(report);
  }

  static async changeSheetVisibility(sheetName: string, visibility: Excel.SheetVisibility) {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getItemOrNullObject(sheetName);
      sheet.visibility = visibility;
      await context.sync();
    });
  }

  static async getDocumentAndUpload(fileName: string, fileType: Office.FileType, fileExtension = "pdf") {
    try {
      const file = await ReportUtil.getDocumentAsCompressed(fileType);
      const fileObject = ReportUtil.getFileObject(fileName, file.fileBytes, fileExtension);

      const fileId = await ReportAPI.uploadFile(fileObject);
      return fileId;
    } catch (error) {
      console.error(error);
      return;
    }
  }

  static getDocumentAsCompressed(type: Office.FileType): Promise<{ fileBytes: []; fileContent }> {
    return new Promise((resolve, reject) => {
      Office.context.document.getFileAsync(type, { sliceSize: 65536 /*64 KB*/ }, function (result) {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          // If the getFileAsync call succeeded, then
          // result.value will return a valid File Object.
          const myFile = result.value;
          const sliceCount = myFile.sliceCount;
          const docDataSlices = [];
          let slicesReceived = 0;
          let gotAllSlices = true;

          // Get the file slices.
          ReportUtil.getSliceAsync(myFile, 0, sliceCount, gotAllSlices, docDataSlices, slicesReceived, resolve, reject);
        } else {
          reject("Unable to read file");
        }
      });
    });
  }

  static getSliceAsync(file, nextSlice, sliceCount, gotAllSlices, docdataSlices, slicesReceived, resolve, reject) {
    file.getSliceAsync(nextSlice, function (sliceResult) {
      if (sliceResult.status == "succeeded") {
        if (!gotAllSlices) {
          reject("Unable to read file");
          return;
        }

        // Got one slice, store it in a temporary array.
        // (Or you can do something else, such as
        // send it to a third-party server.)
        docdataSlices[sliceResult.value.index] = sliceResult.value.data;
        if (++slicesReceived == sliceCount) {
          // All slices have been received.
          file.closeAsync();
          ReportUtil.onGotAllSlices(docdataSlices, resolve);
        } else {
          ReportUtil.getSliceAsync(file, ++nextSlice, sliceCount, gotAllSlices, docdataSlices, slicesReceived, resolve, reject);
        }
      } else {
        gotAllSlices = false;
        file.closeAsync();
        reject("Unable to read file");
      }
    });
  }

  static onGotAllSlices(docdataSlices, resolve) {
    var docdata = [];
    for (var i = 0; i < docdataSlices.length; i++) {
      docdata = docdata.concat(docdataSlices[i]);
    }

    var fileContent = new String();
    for (var j = 0; j < docdata.length; j++) {
      fileContent += String.fromCharCode(docdata[j]);
    }
    resolve({ fileBytes: docdata, fileContent });
  }

  static getFileObject(filename: string, byte: any, fileExtension = "pdf") {
    const data = new Uint8Array(byte);
    const encodedFileName = `${filename}.${fileExtension}`;

    const mimeType = ReportUtil.getMimeType(fileExtension);

    const blob = new Blob([data], { type: mimeType });
    const file = new File([blob], encodedFileName, { type: mimeType });

    return file;
  }

  static getMimeType(fileExtension: string) {
    switch (fileExtension) {
      default:
      case "pdf":
        return "application/pdf";
      case "xlsx":
        return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    }
  }
}
